// ./src/components/generic/TagsSelectorInput/TagsSelectorInput.tsx
import React, { useState, useMemo, useCallback, useRef } from "react";
import {
  Combobox,
  PillsInput,
  Pill,
  Group,
  Text,
  Box,
  useCombobox,
  Loader,
  InputWrapper,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { getMostContrastingColor } from "@/utils/colorHelpers";
import { Tag } from "@/utils/api/TagService";

import { useTagsContext } from "@/contexts/TagsContext";
import { useAppEnvironment } from "@/contexts/AppEnvironmentContext";
import { useCreateInPlace } from "@/hooks/useCreateInPlace";
import { CREATE_IN_OWN_ACCOUNT } from "@/utils/createInPlace";

import { AppIcon } from "../AppIcon";
import { TagsPickerModal } from "./TagsPickerModal";

// ============================================
// Types
// ============================================

export interface TagsSelectorInputProps {
  /** Field name for form binding */
  name?: string;

  /** Current selected tag IDs */
  value: string[];

  /** Callback when selection changes: (name, tagIds) */
  onChange: (name: string, tagIds: string[]) => void;

  /** Field label */
  label?: string;

  /** Placeholder text */
  placeholder?: string;

  /** Max tags allowed (default: 5) */
  maxTags?: number;

  /** Whether selector is disabled */
  disabled?: boolean;

  /** Error message */
  error?: string;

  /** Size */
  size?: "sm" | "md" | "lg";

  /** Whether to allow creating new tags inline */
  allowCreate?: boolean;

  /** Description text below input */
  description?: string;

  /** Whether the field is required */
  required?: boolean;

  /** Whether dropdown should render in a portal (default: true) */
  withinPortal?: boolean;
}

// ============================================
// Constants
// ============================================

const CREATE_OPTION_VALUE = "__create__";
const DEFAULT_MAX_TAGS = 5;

// ============================================
// Helper Components
// ============================================

interface TagPillProps {
  tag: Tag;
  onRemove: () => void;
  disabled?: boolean;
}

const TagPill: React.FC<TagPillProps> = ({ tag, onRemove, disabled }) => {
  const tagColor = tag.color || "#6B7280";

  return (
    <Pill
      withRemoveButton
      onRemove={onRemove}
      disabled={disabled}
      styles={{
        root: {
          backgroundColor: tagColor,
          color: getMostContrastingColor(tagColor),
        },
        remove: {
          color: getMostContrastingColor(tagColor),
        },
      }}
      size="md"
    >
      {tag.name}
    </Pill>
  );
};

interface CreatingPillProps {
  tagName: string;
}

const CreatingPill: React.FC<CreatingPillProps> = ({ tagName }) => {
  return (
    <Pill
      styles={{
        root: {
          backgroundColor: "var(--mantine-color-gray-2)",
        },
      }}
    >
      <Group gap={4} wrap="nowrap">
        <Loader size={10} />
        <span>{tagName}</span>
      </Group>
    </Pill>
  );
};

// ============================================
// Component
// ============================================

const TagsSelectorInputComponent: React.FC<TagsSelectorInputProps> = ({
  name = "tagIds",
  value,
  onChange,
  label,
  placeholder,
  maxTags = DEFAULT_MAX_TAGS,
  disabled = false,
  error,
  size = "md",
  allowCreate: allowCreateProp = true,
  description,
  required = false,
  withinPortal = true,
}) => {
  // #142 — a tag lands in the caller's own account: never offered to a crew member.
  const allowCreate = useCreateInPlace(CREATE_IN_OWN_ACCOUNT) && allowCreateProp;
  const { t } = useTranslation(["tags", "common"]);
  const {
    activeTags,
    loading: tagsLoading,
    getTagById,
    createTagInline,
    isCreating,
  } = useTagsContext();
  const { isMobile } = useAppEnvironment();

  // Local state
  const [searchValue, setSearchValue] = useState("");
  const [creatingTagName, setCreatingTagName] = useState<string | null>(null);
  const [modalOpened, setModalOpened] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Combobox state (desktop only)
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearchValue("");
    },
    onDropdownOpen: () => {
      combobox.focusSearchInput();
    },
  });

  // Get selected tags from context
  const selectedTags = useMemo(() => {
    return value
      .map((id) => getTagById(id))
      .filter((tag): tag is Tag => tag !== undefined);
  }, [value, getTagById]);

  // Check if max tags reached
  const isMaxReached = value.length >= maxTags;

  // Filter available tags (exclude already selected, apply search)
  const filteredTags = useMemo(() => {
    const selectedSet = new Set(value);
    const searchLower = searchValue.toLowerCase().trim();

    return activeTags.filter((tag) => {
      // Exclude already selected
      if (tag.id && selectedSet.has(tag.id)) return false;

      // Apply search filter
      if (searchLower && tag.name) {
        return tag.name.toLowerCase().includes(searchLower);
      }

      return true;
    });
  }, [activeTags, value, searchValue]);

  // Check if exact match exists (for create option)
  const exactMatchExists = useMemo(() => {
    if (!searchValue.trim()) return true;
    const searchLower = searchValue.toLowerCase().trim();
    return activeTags.some((tag) => tag.name?.toLowerCase() === searchLower);
  }, [activeTags, searchValue]);

  // Should show create option
  const showCreateOption =
    allowCreate &&
    searchValue.trim().length > 0 &&
    !exactMatchExists &&
    !isMaxReached;

  // Handle tag selection (desktop combobox)
  const handleSelect = useCallback(
    async (selectedValue: string) => {
      if (isMaxReached) return;

      if (selectedValue === CREATE_OPTION_VALUE) {
        // Create new tag
        const tagName = searchValue.trim();
        if (!tagName) return;

        setCreatingTagName(tagName);
        setSearchValue("");
        combobox.closeDropdown();

        try {
          const newTag = await createTagInline(tagName);
          if (newTag?.id) {
            onChange(name, [...value, newTag.id]);
          }
        } catch (error) {
          console.error("Failed to create tag:", error);
        } finally {
          setCreatingTagName(null);
        }
      } else {
        // Select existing tag
        onChange(name, [...value, selectedValue]);
        setSearchValue("");
      }

      // Keep focus on input for quick multi-select
      inputRef.current?.focus();
    },
    [
      isMaxReached,
      searchValue,
      combobox,
      createTagInline,
      onChange,
      value,
      name,
    ]
  );

  // Handle tag removal
  const handleRemove = useCallback(
    (tagId: string) => {
      onChange(
        name,
        value.filter((id) => id !== tagId)
      );
    },
    [onChange, value, name]
  );

  // Handle keyboard events (desktop only)
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      // Remove last tag on backspace when input is empty
      if (event.key === "Backspace" && searchValue === "" && value.length > 0) {
        event.preventDefault();
        onChange(name, value.slice(0, -1));
      }
    },
    [searchValue, value, onChange, name]
  );

  // Handle modal confirm (mobile)
  const handleModalConfirm = useCallback(
    (tagIds: string[]) => {
      onChange(name, tagIds);
    },
    [name, onChange]
  );

  // Placeholder text
  const inputPlaceholder = useMemo(() => {
    if (isMaxReached) {
      return t("tags:selector.maxReached", {
        max: maxTags,
        defaultValue: `Maximum ${maxTags} tags`,
      });
    }
    return (
      placeholder ||
      t("tags:selector.placeholder", {
        defaultValue: "Select or create tags...",
      })
    );
  }, [isMaxReached, maxTags, placeholder, t]);

  // ⚠ The desktop Combobox's memos run on EVERY render, phone included (#129): declared below the mobile `return`, a
  // window that crossed the breakpoint rendered two hooks more (or fewer) than the last render, and React threw.
  // Render dropdown options (desktop)
  const renderOptions = useMemo(() => {
    const options: React.ReactNode[] = [];

    // Existing tags
    filteredTags.forEach((tag) => {
      if (!tag.id) return;
      const tagColor = tag.color || "#6B7280";

      options.push(
        <Combobox.Option key={tag.id} value={tag.id}>
          <Group gap="sm" wrap="nowrap">
            <Box
              style={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: tagColor,
                flexShrink: 0,
              }}
            />
            <Text size="md" lineClamp={1}>
              {tag.name}
            </Text>
          </Group>
        </Combobox.Option>
      );
    });

    // Create option
    if (showCreateOption) {
      options.push(
        <Combobox.Option key={CREATE_OPTION_VALUE} value={CREATE_OPTION_VALUE}>
          <Group gap="sm" wrap="nowrap">
            <AppIcon iconName="Plus" size={14} />
            <Text size="md">
              {t("tags:selector.createOption", {
                name: searchValue.trim(),
                defaultValue: `Create "${searchValue.trim()}"`,
              })}
            </Text>
          </Group>
        </Combobox.Option>
      );
    }

    return options;
  }, [filteredTags, showCreateOption, searchValue, t]);

  // Empty state message (desktop)
  const emptyMessage = useMemo(() => {
    if (tagsLoading) {
      return (
        <Group gap="xs" justify="center" p="sm">
          <Loader size={14} />
          <Text size="md" c="dimmed">
            {t("common:loading")}
          </Text>
        </Group>
      );
    }

    if (filteredTags.length === 0 && !showCreateOption) {
      if (searchValue.trim()) {
        return (
          <Text size="sm" c="dimmed" ta="center" p="sm">
            {t("common:form.noResults", { defaultValue: "No tags found" })}
          </Text>
        );
      }
      return (
        <Text size="sm" c="dimmed" ta="center" p="sm">
          {t("tags:selector.noTags", { defaultValue: "No tags available" })}
        </Text>
      );
    }

    return null;
  }, [tagsLoading, filteredTags.length, showCreateOption, searchValue, t]);

  // ==========================================
  // Mobile: Read-only pills input + full-screen modal
  // ==========================================
  if (isMobile) {
    return (
      <>
        <InputWrapper
          label={label}
          description={description}
          error={error}
          required={required}
          size={size}
        >
          <PillsInput
            onClick={() => {
              if (!disabled) {
                setModalOpened(true);
              }
            }}
            disabled={disabled}
            error={!!error}
            size={size}
            rightSection={
              tagsLoading || isCreating ? <Loader size={14} /> : undefined
            }
            styles={{
              input: {
                cursor: disabled ? "not-allowed" : "pointer",
              },
            }}
          >
            <Pill.Group>
              {selectedTags.map((tag) => (
                <TagPill
                  key={tag.id}
                  tag={tag}
                  onRemove={() => tag.id && handleRemove(tag.id)}
                  disabled={disabled}
                />
              ))}

              {selectedTags.length === 0 && (
                <Text size="sm" c="dimmed" py={4}>
                  {inputPlaceholder}
                </Text>
              )}
            </Pill.Group>
          </PillsInput>
        </InputWrapper>

        <TagsPickerModal
          opened={modalOpened}
          onClose={() => setModalOpened(false)}
          value={value}
          onConfirm={handleModalConfirm}
          maxTags={maxTags}
          allowCreate={allowCreate}
        />
      </>
    );
  }

  // ==========================================
  // Desktop: Original Combobox behavior
  // ==========================================

  // Render selected pills (desktop)
  const renderPills = selectedTags.map((tag) => (
    <TagPill
      key={tag.id}
      tag={tag}
      onRemove={() => tag.id && handleRemove(tag.id)}
      disabled={disabled}
    />
  ));

  // Add creating pill if in progress (desktop)
  if (creatingTagName) {
    renderPills.push(
      <CreatingPill key="__creating__" tagName={creatingTagName} />
    );
  }

  return (
    <InputWrapper
      label={label}
      description={description}
      error={error}
      required={required}
      size={size}
    >
      <Combobox
        store={combobox}
        onOptionSubmit={handleSelect}
        withinPortal={withinPortal}
      >
        <Combobox.DropdownTarget>
          <PillsInput
            onClick={() =>
              !disabled && !isMaxReached && combobox.openDropdown()
            }
            disabled={disabled}
            error={!!error}
            size={size}
            rightSection={
              tagsLoading || isCreating ? <Loader size={14} /> : undefined
            }
          >
            <Pill.Group>
              {renderPills}

              {!isMaxReached && (
                <Combobox.EventsTarget>
                  <PillsInput.Field
                    ref={inputRef}
                    value={searchValue}
                    onChange={(event) => {
                      setSearchValue(event.currentTarget.value);
                      combobox.openDropdown();
                      combobox.updateSelectedOptionIndex();
                    }}
                    onFocus={() => !disabled && combobox.openDropdown()}
                    onBlur={() => combobox.closeDropdown()}
                    onKeyDown={handleKeyDown}
                    placeholder={
                      value.length === 0 ? inputPlaceholder : undefined
                    }
                    disabled={disabled}
                  />
                </Combobox.EventsTarget>
              )}
            </Pill.Group>
          </PillsInput>
        </Combobox.DropdownTarget>

        <Combobox.Dropdown>
          <Combobox.Options mah={250} style={{ overflowY: "auto" }}>
            {renderOptions.length > 0 ? renderOptions : emptyMessage}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </InputWrapper>
  );
};

export const TagsSelectorInput = React.memo(TagsSelectorInputComponent);